import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHttpClient } from "../../hooks/useHttpClient";
import type { SessionDetailsResponse } from "../../interfaces/sessionDetails";
import { useLocalization } from "../../stores/localizationContext";
import { useUserContext } from "../../stores/userContext";
import { getResourceGroupByType } from "../../utils/sessionEducationalResources";
import { SessionDetailsHeader } from "./SessionDetailsHeader";
import { SessionExamsSection } from "./SessionExamsSection";
import { SessionHandoutsSection } from "./SessionHandoutsSection";
import { SessionLiveRecording } from "./SessionLiveRecording";
import { SessionStudentReport } from "./SessionStudentReport";
import { SessionWorksheetsSection } from "./SessionWorksheetsSection";
import "./SessionDetailsPage.scss";

export const SessionDetailsPage: FC = () => {
  const { classId: classIdParam, sessionId: sessionIdParam } = useParams<{
    classId: string;
    sessionId: string;
  }>();
  const { lang, t } = useLocalization();
  const { user } = useUserContext();
  const api = useHttpClient();
  const navigate = useNavigate();

  const [details, setDetails] = useState<SessionDetailsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.userId || !classIdParam || !sessionIdParam) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const template = import.meta.env.VITE_GET_SESSION_DETAILS as string;
        const endpoint = template
          .replace("{educator_id}", user.userId)
          .replace("{class_id}", classIdParam)
          .replace("{session_id}", sessionIdParam);
        const res = await api.get<SessionDetailsResponse>(endpoint);
        if (!cancelled) setDetails(res.data);
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response
          ?.status;
        if (status === 403 || status === 404) {
          navigate(`/${lang}`, { replace: true });
          return;
        }
        if (!cancelled) {
          setLoadError(
            (e as Error)?.message ?? "Failed to load session"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, classIdParam, lang, navigate, sessionIdParam, user?.userId]);

  if (loading) {
    return (
      <div className="session-details-page">
        <div className="session-details-page__state">
          {t("loading", "Loading…")}
        </div>
      </div>
    );
  }

  if (loadError || !details) {
    return (
      <div className="session-details-page">
        <div className="session-details-page__state">
          {loadError ??
            t("session_details_unavailable", "Session could not be loaded.")}
        </div>
      </div>
    );
  }

  const classIdNum = Number(classIdParam);
  const sessionIdNum = Number(sessionIdParam);
  const recordings = getResourceGroupByType(details, "recordings");
  const handouts = getResourceGroupByType(details, "handouts");
  const worksheets = getResourceGroupByType(details, "worksheets");
  const exams = getResourceGroupByType(details, "exams");
  const subject = details.subjects[0];

  return (
    <div className="session-details-page">
      <SessionDetailsHeader details={details} />
      <div className="session-details-page__materials">
        <div className="container">
          <SessionLiveRecording
            recordings={recordings}
            sessionTitle={details.session_title}
            subjectLabel={subject?.subject_name}
          />
          <SessionStudentReport
            language={lang}
            classId={classIdNum}
            sessionId={sessionIdNum}
          />
          <SessionHandoutsSection items={handouts} />
          <SessionWorksheetsSection items={worksheets} />
          <SessionExamsSection items={exams} />
        </div>
      </div>
    </div>
  );
};
